import { useEffect, useState } from 'react';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutwardRounded';
import SouthIcon from '@mui/icons-material/SouthRounded';
import Images from '../utility/Images';
import { useMagnetic, useTilt } from '../utility/motion';

const ROTATING_WORDS = {
    it: ['backend solidi', 'app mobile', 'siti web', 'gestionali', 'strumenti utili'],
    en: ['solid backends', 'mobile apps', 'websites', 'management tools', 'useful tools'],
};

const ORBIT_BADGES = ['Java', 'Spring', 'React', 'Node', 'Docker', 'SQL'];

function SplitText({text, baseDelay = 0}) {
    return (
        <span className="split" aria-label={text}>
            {text.split('').map((char, index) => (
                <span
                    aria-hidden="true"
                    key={`${char}-${index}`}
                    className="split__char"
                    style={{ '--i': index, '--base': `${baseDelay}ms` }}
                >
                    {char === ' ' ? ' ' : char}
                </span>
            ))}
        </span>
    );
}

function RotatingWord({words}) {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        setIndex(0);
        const timer = setInterval(() => setIndex((current) => (current + 1) % words.length), 2400);
        return () => clearInterval(timer);
    }, [words]);

    return (
        <span className="rotator" aria-live="polite">
            <span className="rotator__sizer" aria-hidden="true">
                {words.reduce((longest, word) => (word.length > longest.length ? word : longest), '')}
            </span>
            {words.map((word, wordIndex) => (
                <span
                    key={word}
                    className={`rotator__word ${wordIndex === index ? 'is-active' : ''} ${
                        wordIndex === (index - 1 + words.length) % words.length ? 'is-leaving' : ''
                    }`}
                    aria-hidden={wordIndex !== index}
                >
                    {word}
                </span>
            ))}
        </span>
    );
}

function Hero({checked, onNavigate}) {
    const [isWiggling, setIsWiggling] = useState(false);
    const portraitRef = useTilt(14);
    const primaryRef = useMagnetic(0.22);
    const secondaryRef = useMagnetic(0.22);
    const words = checked ? ROTATING_WORDS.it : ROTATING_WORDS.en;

    const handleProfileClick = () => {
        if (isWiggling) return;
        setIsWiggling(true);
        setTimeout(() => setIsWiggling(false), 2500);
    };

    return (
        <section className="hero" id="about" aria-labelledby="hero-title">
            <div className="hero__copy">
                <p className="hero__status hero-in" style={{ '--d': '0ms' }}>
                    <span className="pulse-dot" aria-hidden="true" />
                    {checked ? 'Disponibile per nuove opportunità' : 'Available for new opportunities'}
                </p>

                <h1 className="hero__title" id="hero-title">
                    <SplitText text="Flavio" baseDelay={120} />
                    <br />
                    <span className="hero__title-accent">
                        <SplitText text="Ceccarelli" baseDelay={360} />
                    </span>
                </h1>

                <p className="hero__lead hero-in" style={{ '--d': '760ms' }}>
                    {checked ? 'Costruisco ' : 'I build '}
                    <RotatingWord words={words} />
                </p>

                <p className="hero__role hero-in" style={{ '--d': '880ms' }}>
                    <span className="mono">Java Backend · Senior Consultant</span>
                    <span className="hero__role-sep" aria-hidden="true" />
                    <span className="mono hero__company">@ Softlab s.p.a.</span>
                </p>

                <div className="hero__actions hero-in" style={{ '--d': '1000ms' }}>
                    <a
                        ref={primaryRef}
                        href="#/work"
                        className="btn btn--primary magnetic"
                        onClick={(event) => onNavigate(event, 'work')}
                    >
                        <span>{checked ? 'Guarda i lavori' : 'See my work'}</span>
                        <ArrowOutwardIcon className="btn__icon" />
                    </a>
                    <a
                        ref={secondaryRef}
                        href="#/contacts"
                        className="btn btn--ghost magnetic"
                        onClick={(event) => onNavigate(event, 'contacts')}
                    >
                        <span>{checked ? 'Scrivimi' : 'Get in touch'}</span>
                    </a>
                </div>
            </div>

            <div className="hero__visual hero-in" style={{ '--d': '300ms' }}>
                <div className="portrait tilt" ref={portraitRef}>
                    <div className="portrait__halo" aria-hidden="true" />
                    <div className="portrait__ring portrait__ring--outer" aria-hidden="true">
                        {ORBIT_BADGES.map((badge, index) => (
                            <span
                                key={badge}
                                className="orbit-badge"
                                style={{ '--angle': `${(360 / ORBIT_BADGES.length) * index}deg` }}
                            >
                                <span className="orbit-badge__inner">{badge}</span>
                            </span>
                        ))}
                    </div>
                    <div className="portrait__ring portrait__ring--inner" aria-hidden="true" />
                    <button
                        type="button"
                        className={`portrait__photo ${isWiggling ? 'wiggle-animation' : ''}`}
                        onClick={handleProfileClick}
                        aria-label={checked ? 'Foto profilo' : 'Profile picture'}
                    >
                        <img src={Images.memoji} alt="Flavio Ceccarelli" draggable="false" />
                        <span className="portrait__shine" aria-hidden="true" />
                    </button>
                    {isWiggling && <span className="portrait__bubble">Haha, smettila! 😄</span>}
                </div>
            </div>

            <a
                href="#/about-me"
                className="hero__scroll hero-in"
                style={{ '--d': '1300ms' }}
                onClick={(event) => {
                    event.preventDefault();
                    document.getElementById('about-me')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                aria-label={checked ? 'Scorri giù' : 'Scroll down'}
            >
                <span className="hero__scroll-track"><span className="hero__scroll-thumb" /></span>
                <SouthIcon className="hero__scroll-icon" />
            </a>
        </section>
    );
}

export default Hero;
